import { useMemo } from 'react'
import { DjayAccessory, DjayTier } from './DjayAvatar'
import {
  ACHIEVEMENTS,
  type AchievementCheckData,
} from './achievements/achievementData'
import { MODULES } from '../content'
import { VariantProgress } from './useVariantUnlocks'

// NOTE: the customization hook reads the achievement-check subset of the unlock
// domain. `AchievementCheckData` is the documented base of the shared
// `UnlockContext` (see `unlock/unlockContext.ts`); the variant-unlock hook
// consumes the full `UnlockContext` superset. Both hooks therefore read one
// shared facts-set instead of two parallel ad-hoc shapes (audit §9.4).

export type AccessoryCategory = 'hoofd' | 'gezicht' | 'hals' | 'hand' | 'effect'

export interface AccessoryUnlockInfo {
  id: DjayAccessory
  name: string
  unlocked: boolean
  condition: string
  progress: VariantProgress
  category: AccessoryCategory | 'none'
}

export interface TierUnlockInfo {
  id: DjayTier
  name: string
  unlocked: boolean
  condition: string
  progress: VariantProgress
}

// Map accessory IDs to human-readable names
export const ACCESSORY_NAMES: Record<DjayAccessory, string> = {
  none: 'Geen',
  seedling: 'Sprietje',
  pencil: 'Potlood',
  badge100: '100 Badge',
  smallFlame: 'Vlammetje',
  trophy: 'Trofee',
  doubleFlameCrown: 'Vuurkroon',
  starWand: 'Sterrenstaf',
  tripleStars: 'Drie Sterren',
  scepter: 'Scepter',
  leafGarland: 'Lauwerkrans',
  medal: 'Medaille',
  cosmicOrb: 'Kosmische Bol',
  shadowOrb: 'Schaduw Bol',
  sunglasses: 'Zonnebril',
  cap: 'Petje',
  goldChain: 'Gouden Ketting',
}

export const ACCESSORY_CATEGORIES: Record<
  DjayAccessory,
  AccessoryCategory | 'none'
> = {
  none: 'none',
  seedling: 'hand',
  pencil: 'hand',
  badge100: 'hals',
  smallFlame: 'hand',
  trophy: 'hand',
  doubleFlameCrown: 'hoofd',
  starWand: 'hand',
  tripleStars: 'effect',
  scepter: 'hand',
  leafGarland: 'hals',
  medal: 'hals',
  cosmicOrb: 'hand',
  shadowOrb: 'hand',
  sunglasses: 'gezicht',
  cap: 'hoofd',
  goldChain: 'hals',
}

/**
 * Labels for the ACCESSORY categories (head/face/neck/hand/effect).
 * Renamed from `CATEGORY_LABELS` (audit cleanup) to avoid a name collision with
 * the ACHIEVEMENT-category `CATEGORY_LABELS` exported from
 * `mascot/achievements/achievementData.ts` — two different label maps that
 * previously shared a name across the unlockable system.
 */
export const ACCESSORY_CATEGORY_LABELS: Record<AccessoryCategory, string> = {
  hoofd: 'Hoofd',
  gezicht: 'Gezicht',
  hals: 'Hals',
  hand: 'In de hand',
  effect: 'Effect',
}

// Map tier IDs to human-readable names
const TIER_NAMES: Record<DjayTier, string> = {
  rookie: 'Beginner',
  pro: 'Pro',
  master: 'Meester',
}

export function useDjayCustomization(
  checkData: AchievementCheckData,
  unlockedCosmetics: string[] = [],
) {
  return useMemo(() => {
    // 1. Accessories
    const accessoryMap = new Map<DjayAccessory, AccessoryUnlockInfo>()

    // Always add 'none'
    accessoryMap.set('none', {
      id: 'none',
      name: ACCESSORY_NAMES.none,
      unlocked: true,
      condition: 'Altijd beschikbaar',
      progress: { current: 1, max: 1, label: 'Ontgrendeld' },
      category: 'none',
    })

    // Map from achievements
    ACHIEVEMENTS.forEach((achievement) => {
      if (achievement.accessory && achievement.accessory !== 'none') {
        const isUnlocked = achievement.check(checkData)
        const progress = achievement.getProgress
          ? achievement.getProgress(checkData)
          : { current: isUnlocked ? 1 : 0, max: 1, label: 'Ontgrendeld' }

        // Curated reward-card model: each cosmetic accessory is granted by
        // exactly ONE achievement (the rest are pure achievement cards with
        // `accessory: 'none'`). This guard is therefore a 1:1 mapping in
        // practice; the unlocked-wins tie-break only survives as a defensive
        // no-op should a future card ever re-share an accessory.
        const existing = accessoryMap.get(achievement.accessory)
        if (!existing || (!existing.unlocked && isUnlocked)) {
          accessoryMap.set(achievement.accessory, {
            id: achievement.accessory,
            name:
              ACCESSORY_NAMES[achievement.accessory] || achievement.accessory,
            unlocked: isUnlocked,
            condition: achievement.description,
            progress,
            category: ACCESSORY_CATEGORIES[achievement.accessory] || 'hand',
          })
        }
      }
    })

    // Standalone wearables with their own simple, earnable gates.
    // These reuse existing checkData signals (NOT variant thresholds or
    // mastery logic) so each shows a real "Ontgrendel door…" path + progress
    // bar on the front UI. The gate definitions live in the single
    // STANDALONE_COSMETIC_GATE_DEFS source below — both this UI progress view
    // and the persistence predicate (STANDALONE_COSMETIC_GATES) derive from it,
    // so the thresholds can never drift (audit §9.5 fix).
    STANDALONE_COSMETIC_GATE_DEFS.forEach((gate) => {
      if (!accessoryMap.has(gate.id)) {
        const current = Math.min(gate.stat(checkData), gate.max)
        // Unlocked if the live stat gate is met OR it was previously earned and
        // persisted (so a cosmetic stays "owned" even if a live stat dips, e.g.
        // currentWeekDays resetting on a new week).
        const gateMet = current >= gate.max
        const persisted = unlockedCosmetics.includes(gate.id)
        const unlocked = gateMet || persisted
        accessoryMap.set(gate.id, {
          id: gate.id,
          name: ACCESSORY_NAMES[gate.id],
          unlocked,
          condition: gate.condition,
          progress: {
            current: unlocked ? gate.max : current,
            max: gate.max,
            label: unlocked ? 'Ontgrendeld' : gate.label,
          },
          category: ACCESSORY_CATEGORIES[gate.id],
        })
      }
    })

    const accessories = Array.from(accessoryMap.values())

    // 2. Tiers
    const tiers: TierUnlockInfo[] = [
      {
        id: 'rookie',
        name: TIER_NAMES.rookie,
        unlocked: true,
        condition: 'Altijd beschikbaar',
        progress: { current: 1, max: 1, label: 'Ontgrendeld' },
      },
      {
        id: 'pro',
        name: TIER_NAMES.pro,
        unlocked: checkData.masteredCount >= 1 && checkData.level >= 5,
        condition: 'Level 5 bereikt & 1 vak afgerond',
        progress: {
          current:
            (checkData.level >= 5 ? 1 : 0) +
            (checkData.masteredCount >= 1 ? 1 : 0),
          max: 2,
          label: 'doelen',
        },
      },
      {
        id: 'master',
        name: TIER_NAMES.master,
        unlocked:
          checkData.masteredCount >= MODULES.length && checkData.level >= 15,
        condition: `Level 15 bereikt & alle ${MODULES.length} vakken afgerond`,
        progress: {
          current:
            (checkData.level >= 15 ? 1 : 0) +
            (checkData.masteredCount >= MODULES.length ? 1 : 0),
          max: 2,
          label: 'doelen',
        },
      },
    ]

    return {
      accessories,
      tiers,
      unlockedAccessoriesCount: accessories.filter((a) => a.unlocked).length,
      unlockedTiersCount: tiers.filter((t) => t.unlocked).length,
    }
  }, [checkData, unlockedCosmetics])
}

/**
 * Single source of truth for the standalone (non-achievement) cosmetics and
 * their live stat gates. Both the in-hook progress view (above) and the
 * persistence predicate (STANDALONE_COSMETIC_GATES, below) derive from this, so
 * a threshold lives in exactly one place (audit §9.5 — was previously
 * duplicated across the in-memo array and the exported predicate, which could
 * drift the UI progress bar out of sync with the persistence gate).
 */
interface StandaloneCosmeticGateDef {
  id: DjayAccessory
  condition: string
  max: number
  label: string
  stat: (checkData: AchievementCheckData) => number
}

const STANDALONE_COSMETIC_GATE_DEFS: StandaloneCosmeticGateDef[] = [
  {
    id: 'cap',
    condition: 'Beantwoord 25 vragen',
    max: 25,
    label: 'vragen',
    stat: (d) => d.totalQuestionsAnswered,
  },
  {
    id: 'sunglasses',
    condition: 'Bereik level 3',
    max: 3,
    label: 'level',
    stat: (d) => d.level,
  },
  {
    id: 'goldChain',
    condition: 'Haal 5 keer je dagdoel',
    max: 5,
    label: 'dagdoelen',
    stat: (d) => d.totalDailyGoalsReached,
  },
]

/**
 * The standalone cosmetic ids and their "is this gate met" predicate, derived
 * from STANDALONE_COSMETIC_GATE_DEFS. Used by the collection page to persist a
 * cosmetic the moment its gate is first met (via markCosmeticUnlocked).
 */
export const STANDALONE_COSMETIC_GATES: {
  id: DjayAccessory
  isMet: (checkData: AchievementCheckData) => boolean
}[] = STANDALONE_COSMETIC_GATE_DEFS.map((gate) => ({
  id: gate.id,
  isMet: (d) => gate.stat(d) >= gate.max,
}))
